import { Checkbox, Divider, Group, ScrollArea, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import dayjs from 'dayjs';
import { useEffect } from 'react';
import type { AllocationOfCostsChartResponseByCategory } from '../../../../api/financials-client/financials-client.type';
import { snakeCaseToTitleCase } from '../../../../helpers/string-helpers/string-helpers';
import { useFinancialsStore } from '../../../../store/financials-store/financials-store';
import {
  setAllocationOfCostsMeasure,
  setAllocationOfCostsOptions,
  setAllocationOfCostsTrendLines,
} from '../../../../store/financials-store/financials-store.actions';
import type { AllocationOfCostsMeasures } from '../../../../store/financials-store/financials-store.type';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import { newCOLORS } from '../../../../styles/colors';
import { skyBase } from '../../../../styles/design-tokens';
import { Icon } from '../../../../ui-library/icon/icon';
import { Select } from '../../../../ui-library/select/select';
import { DEFAULT_CHART_COLORS } from '../../constants';
import { useAllocationOfCosts } from '../../financials.hooks';
import { SmallSquareButton } from '../shared/small-square-button';
import { buildMeasureSelectOptions, getIconNameForMeasure } from './allocation-of-costs.helpers';

function Filters({ setShow }: { setShow: (show: boolean) => void }) {
  const [isMeasuresOpen, { toggle: toggleMeasures }] = useDisclosure(true);
  const [isTrendsOpen, { toggle: toggleTrends }] = useDisclosure(true);
  const portfolio = useGlobalStore((state) => state.portfolio);
  const teamId = useFinancialsStore((state) => state.team);
  const boardId = useFinancialsStore((state) => state.board);
  const startDate = useFinancialsStore((state) => state.startDate);
  const endDate = useFinancialsStore((state) => state.endDate);
  const {
    filters: {
      allocationOfCosts: { selectedMeasure, selectedOptions, selectedTrendLines },
    },
  } = useFinancialsStore();

  const { data } = useAllocationOfCosts(
    dayjs(startDate).toDate(),
    dayjs(endDate).toDate(),
    portfolio?.id || null,
    teamId,
    boardId,
  );

  useEffect(() => {
    if (!selectedMeasure) {
      setAllocationOfCostsMeasure('assignee');
    }
  }, [selectedMeasure]);

  return (
    <Stack h="100%">
      <Group>
        <span style={{ flex: 1 }}>
          <Select
            value={selectedMeasure}
            options={buildMeasureSelectOptions(data || ({} as AllocationOfCostsChartResponseByCategory))}
            placeholder="Select Cost Category"
            onChange={(value) => {
              setAllocationOfCostsMeasure(value as AllocationOfCostsMeasures);
              setAllocationOfCostsOptions([]);
              setAllocationOfCostsTrendLines([]);
            }}
          />
        </span>
        <SmallSquareButton onClick={() => setShow(false)} iconName="arrow_back_ios" />
      </Group>
      <Divider />
      <Group justify="space-between">
        <Group>
          <Icon
            name={selectedMeasure ? getIconNameForMeasure(selectedMeasure) : 'adjust'}
            size={24}
            color={newCOLORS.gray}
          />
          <Text fw={750}>{selectedMeasure ? snakeCaseToTitleCase(selectedMeasure) : 'Measures'}</Text>
        </Group>
        <Icon
          name={isMeasuresOpen ? 'unfold_less' : 'unfold_more'}
          size={24}
          color={newCOLORS.gray}
          onClick={toggleMeasures}
          style={{ cursor: 'pointer' }}
        />
      </Group>
      <Divider />
      <ScrollArea h="100%" scrollbarSize={0} style={{ flex: 1 }}>
        {data && selectedMeasure && selectedMeasure in data && isMeasuresOpen ? (
          <Checkbox.Group
            value={selectedOptions}
            onChange={(value) => setAllocationOfCostsOptions(value as AllocationOfCostsMeasures[])}
          >
            {Object.keys(data[selectedMeasure].options).map((option) => {
              const index = selectedOptions.findIndex((o) => o === option);
              const color = index <= DEFAULT_CHART_COLORS.length - 1 ? DEFAULT_CHART_COLORS[index] : skyBase;
              return (
                <Checkbox
                  key={option}
                  value={option}
                  label={option}
                  mb={8}
                  color={color}
                  styles={{
                    input: { cursor: 'pointer' },
                  }}
                  disabled={selectedOptions.length >= DEFAULT_CHART_COLORS.length && !selectedOptions.includes(option)}
                />
              );
            })}
          </Checkbox.Group>
        ) : null}
        {selectedOptions.length > 0 && (
          <Stack>
            <Divider />
            <Group justify="space-between">
              <Group>
                <Icon name="filter_tilt_shift" size={24} color={newCOLORS.gray} />
                <Text fw={750}>Trends</Text>
              </Group>
              <Icon
                name={isTrendsOpen ? 'unfold_less' : 'unfold_more'}
                size={24}
                color={newCOLORS.gray}
                onClick={toggleTrends}
                style={{ cursor: 'pointer' }}
              />
            </Group>
            <Divider />
            {isTrendsOpen && data && (
              <Checkbox.Group
                value={selectedTrendLines}
                onChange={(value) => setAllocationOfCostsTrendLines(value as AllocationOfCostsMeasures[])}
              >
                {Object.keys(data[selectedMeasure].options)
                  .filter((option) => selectedOptions.includes(option))
                  .map((option) => {
                    const index = selectedTrendLines.findIndex((o) => o === option);
                    const color = index <= DEFAULT_CHART_COLORS.length - 1 ? DEFAULT_CHART_COLORS[index] : skyBase;
                    return (
                      <Checkbox
                        key={option}
                        value={option}
                        label={option}
                        mb={8}
                        color={color}
                        styles={{
                          input: { cursor: 'pointer' },
                        }}
                        disabled={
                          selectedTrendLines.length >= DEFAULT_CHART_COLORS.length &&
                          !selectedTrendLines.includes(option)
                        }
                      />
                    );
                  })}
              </Checkbox.Group>
            )}
          </Stack>
        )}
      </ScrollArea>
    </Stack>
  );
}

export { Filters };
